import React, { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import SelectPlan from "./SelectPlan";
import OnboardContext from "./context";
import AppContext from "../../AppContext";
import Info from "./Info";
import Address from "./Address";
import Avatar from "./Avatar";
import Works from "./Interest/Works";
import Spaces from "./Interest/Spaces";
import Styles from "./Interest/Styles";
import DBD from "./dbd";

// * Styles
import "../../styles/onboard.scss";
import { Processing } from "./Processing";
import Certificate from "./Ceritificate";
import useUsers from "../../hooks/useUsers";
import { toast } from "react-toastify";
import _ from "lodash";
import { getDate, getMonth, getYear } from "date-fns";
import { TermAndAgreement } from "../TermAndAgreement";
import Payment from "./Payment";
import { useNavigate } from "react-router-dom";

// * Steps
const STEP = {
  PLAN: 1,
  INFORMATION: 2,
  ADDRESS: 3,
  DBD: 4,
  CERTIFICATE: 5,
  AVATAR: 6,
  INTERESTS_WORK: 7,
  INTERESTS_SPACE: 8,
  INTERESTS_STYLE: 9,
  PAYMENT: 10,
  PROCESSING: 11,
};

const forbiddenChangePlanSteps = [
  // STEP.DBD,
  // STEP.CERTIFICATE,
  STEP.INTERESTS_WORK,
  STEP.INTERESTS_SPACE,
  STEP.INTERESTS_STYLE,
];

const PAGE_STATE = {
  LOADING: 1,
  IDLE: 2,
  ERROR: 3,
};

const Onboard = ({ navigate, currentPlan }) => {
  const { isAuth, user } = useContext(AppContext);
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const [plans, setPlans] = useState(undefined);
  const [selectedPlan, setSelectedPlan] = useState(undefined);
  const [step, setStep] = useState(STEP.PLAN);
  const [information, setInformation] = useState({});
  const [works, setWorks] = useState(undefined);
  const [spaces, setSpaces] = useState(undefined);
  const [styles, setStyles] = useState(undefined);
  const { queryPlans, queryDesignWorks, queryDesignSpaces, queryDesignStyles } =
    useUsers();
  const [isDisplayTerms, setIsDisplayTerms] = useState(
    currentPlan ? false : true
  );
  const [canSelectTrial, setCanSelectTrial] = useState(true);
  const _navigate = useNavigate();

  useEffect(() => {
    init();

    return () => {};
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const init = async () => {
    // * Get user credentials
    const credentials = localStorage.getItem("onboard:credentials");
    const registrationId = localStorage.getItem("onboard:registration-id");

    if (!currentPlan && (!credentials || !registrationId)) {
      toast.error("No credentials or registration found");
      _navigate("/");
      return;
    }

    const _credentials = JSON.parse(credentials);

    // * Set information for change plan
    let _information = _.cloneDeep(information);

    if (currentPlan) {
      const { firstname, surname, dateOfBirth, contact, student, taxId } = user;

      const _dateOfBirth = new Date(dateOfBirth);

      const _day = getDate(_dateOfBirth);
      const _month = getMonth(_dateOfBirth) + 1;
      const _year = getYear(_dateOfBirth);

      const _address = contact?.address ?? {};

      const {
        code: studentId,
        university,
        major,
        degree,
        startingYear,
        endingYear,
      } = student ?? {};

      _information = {
        info: {
          firstname,
          lastname: surname,
          taxId,
          occupation: "",
          experience: "",
          education: "",
          company: "",
          services: [],
          phoneNo: "",
          day: _day.toString(),
          month: _month.toString(),
          year: _year.toString(),
          // * Student
          studentId,
          university,
          startingYear,
          endingYear,
          major,
          degree,
        },
        address: _address,
      };
    }

    setInformation({
      ..._information,
      credentials: _credentials,
      registrationId,
    });

    // * ===== Get plans, works, spaces & styles =====
    // * Plans
    let { data: _plans } = await queryPlans(null, null, -1);
    // * Design Works
    const { data: _works } = await queryDesignWorks(null, null, -1);
    // * Design Spaces
    const { data: _spaces } = await queryDesignSpaces(null, null, -1);
    // * Design Styles
    const { data: _styles } = await queryDesignStyles(null, null, -1);

    if (
      currentPlan &&
      user?.trialEndOn &&
      _plans.find((p) => p._id === currentPlan)?.name !== "Trial"
    ) {
      _plans = _plans.filter((plan) => plan.name !== "Trial");
    }

    setPlans(_plans);
    setWorks(_works);
    setSpaces(_spaces);
    setStyles(_styles);
    setPageState(PAGE_STATE.IDLE);
  };

  const _getCurrentStepIndex = () =>
    selectedPlan.steps.findIndex((s) => s === step);

  const handleNextStep = () => {
    function _validateAndGetActualNextStep(nextStep, index) {
      // * If new user then skip payment step
      if (nextStep === STEP.PAYMENT && !currentPlan) {
        return _validateAndGetActualNextStep(
          selectedPlan.steps[index + 1],
          index + 1
        );
      }

      // * Check trial has been used then don't skip payment otherwise skip
      if (nextStep === STEP.PAYMENT && currentPlan) {
        if (user.trialUsed || new Date(user.trialEndOn) <= new Date()) {
          return nextStep;
        }

        return _validateAndGetActualNextStep(
          selectedPlan.steps[index + 1],
          index + 1
        );
      }

      if (!currentPlan) {
        return nextStep;
      }

      if (
        currentPlan &&
        selectedPlan.name !== "Business" &&
        nextStep === STEP.AVATAR
      ) {
        // * Skip this next step and recursively validate again
        return _validateAndGetActualNextStep(
          selectedPlan.steps[index + 1],
          index + 1
        );
      }

      // * Check if next step isn't included in forbidden steps for when changing plans
      const _isForbidden = forbiddenChangePlanSteps.includes(nextStep);
      if (currentPlan && !_isForbidden) {
        return nextStep;
      }

      return _validateAndGetActualNextStep(
        selectedPlan.steps[index + 1],
        index + 1
      );
    }

    const currentIndex = _getCurrentStepIndex();

    // * Skip Avatar, Interests, Certificate and DBD if changing plan
    const _actualNextStep = _validateAndGetActualNextStep(
      selectedPlan.steps[currentIndex + 1],
      currentIndex + 1
    );

    setStep(_actualNextStep);
  };

  const handlePreviousStep = () => {
    function _validateAndGetActualPreviousStep(step, index) {
      if (!currentPlan) {
        return step;
      }

      if (currentPlan && step === STEP.AVATAR) {
        // * Skip this next step and recursively validate again
        return _validateAndGetActualPreviousStep(
          selectedPlan.steps[index - 1],
          index - 1
        );
      }

      // * Check if next step isn't included in forbidden steps for when changing plans
      if (currentPlan && !forbiddenChangePlanSteps.includes(step)) {
        return step;
      }

      // * Skip this next step and recursively validate again
      return _validateAndGetActualPreviousStep(
        selectedPlan.steps[index - 1],
        index - 1
      );
    }

    const currentIndex = _getCurrentStepIndex();

    if (currentIndex === 0) {
      setStep(STEP.PLAN);
      return;
    }

    // * Skip Avatar, Interests, Certificate and DBD if changing plan
    const _actualPreviousStep = _validateAndGetActualPreviousStep(
      selectedPlan.steps[currentIndex - 1],
      currentIndex - 1
    );

    setStep(_actualPreviousStep);
  };

  const getStepToDisplay = () => {
    if (step === STEP.PLAN) {
      return <SelectPlan />;
    }

    if (step === STEP.INFORMATION) {
      return <Info />;
    }

    if (step === STEP.ADDRESS) {
      return <Address />;
    }

    if (step === STEP.DBD) {
      return <DBD />;
    }

    if (step === STEP.CERTIFICATE) {
      return <Certificate />;
    }

    if (step === STEP.AVATAR) {
      return <Avatar />;
    }

    if (step === STEP.INTERESTS_WORK) {
      return <Works />;
    }

    if (step === STEP.INTERESTS_SPACE) {
      return <Spaces />;
    }

    if (step === STEP.INTERESTS_STYLE) {
      return <Styles />;
    }

    if (step === STEP.PAYMENT) {
      return <Payment />;
    }

    if (step === STEP.PROCESSING) {
      return <Processing />;
    }
  };

  const handleSelectPlan = (e) => {
    setSelectedPlan(e);

    // * Continue to info page
    setStep(STEP.INFORMATION);
  };

  const handleAddInformation = (e) => {
    let _information = information;

    // * Get key and set only that key in information
    for (const key in e) {
      if (!Object.hasOwnProperty.call(e, key)) continue;
      _information[key] = e[key];
    }

    setInformation(_information);
  };

  const handleTermsClose = () => {
    navigate(-1);
  };

  const handleTermsAgreed = () => {
    setIsDisplayTerms(false);
    handleSelectPlan(plans.find((p) => p.name === "Guest"));
  };

  const contextValues = useMemo(
    () => ({
      step,
      plans,
      currentPlan,
      selectedPlan,
      information,
      addInformation: handleAddInformation,
      goForward: handleNextStep,
      goBack: handlePreviousStep,
      setSelectedPlan: handleSelectPlan,
      navigate: navigate,
      designWorks: works,
      designSpaces: spaces,
      designStyles: styles,
    }),
    [step, plans, currentPlan, selectedPlan, information, works, spaces, styles]
  );

  // TODO: Handle error
  if (pageState !== PAGE_STATE.IDLE) {
    return <></>;
  }

  return (
    <OnboardContext.Provider value={contextValues}>
      <div className="onboard">{getStepToDisplay()}</div>
      {isDisplayTerms && (
        <TermAndAgreement
          content={"Legal Information & Notices"}
          onClose={handleTermsClose}
          onAgree={handleTermsAgreed}
        />
      )}
    </OnboardContext.Provider>
  );
};

Onboard.propTypes = {
  navigate: PropTypes.func,
};

Onboard.defaultProps = {
  navigate: (path) => {},
};

export default Onboard;
